import React, { useState, useEffect } from 'react';
import {
  CalendarIcon,
  ClockIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { schedulesAPI, employeeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Schedules = () => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [unscheduledDays, setUnscheduledDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Date range for viewing schedules
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    template_id: '',
    start_time: '',
    end_time: '',
    break_duration_minutes: 0,
    is_working_day: true,
    schedule_type: 'regular',
    comments: ''
  });

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  // Bulk operations state
  const [bulkOperationType, setBulkOperationType] = useState('create');
  const [bulkForm, setBulkForm] = useState({
    employee_ids: [],
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
    template_id: '',
    pattern_id: '',
    schedule_type: 'regular',
    comments: '',
    options: {
      skip_weekends: true,
      skip_existing: true,
      overwrite_existing: false
    }
  });
  const [copyForm, setCopyForm] = useState({
    source_start_date: new Date().toISOString().split('T')[0],
    source_end_date: new Date(new Date().setDate(new Date().getDate() + 6)).toISOString().split('T')[0],
    target_start_date: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0],
    employee_ids: [],
    options: {
      overwrite_existing: false
    }
  });
  const [weeklyPatterns, setWeeklyPatterns] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);

  const { user, isAdmin, isManager, isHRManager } = useAuth();

  // Tab configuration
  const tabs = [
    {
      id: 'calendar',
      name: 'Schedule Calendar',
      icon: CalendarIcon,
      description: 'View and manage employee schedules'
    },
    {
      id: 'templates',
      name: 'Schedule Templates',
      icon: ClockIcon,
      description: 'Manage reusable schedule templates'
    },
    {
      id: 'unscheduled',
      name: 'Unscheduled Days',
      icon: ExclamationTriangleIcon,
      description: 'Review unscheduled working days'
    },
    {
      id: 'bulk',
      name: 'Bulk Operations',
      icon: UserGroupIcon,
      description: 'Apply schedules to multiple employees'
    }
  ];

  // Load initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load schedules when date range changes
  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchSchedules();
    } else if (activeTab === 'unscheduled') {
      fetchUnscheduledDays();
    }
  }, [dateRange, activeTab]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [templatesResponse, employeesResponse, patternsResponse] = await Promise.all([
        schedulesAPI.getTemplates(),
        employeeAPI.getAll({}),
        schedulesAPI.getWeeklyPatterns()
      ]);
      
      setTemplates(templatesResponse.data.templates || []);
      setEmployees(employeesResponse.data.employees || []);
      setWeeklyPatterns(patternsResponse.data.patterns || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await schedulesAPI.getSchedules(dateRange);
      setSchedules(response.data.schedules || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setError('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnscheduledDays = async () => {
    try {
      setLoading(true);
      const response = await schedulesAPI.getUnscheduledDays(dateRange);
      setUnscheduledDays(response.data.unscheduled_working_days || []);
    } catch (error) {
      console.error('Error fetching unscheduled days:', error);
      setError('Failed to load unscheduled days');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      setLoading(true);
      
      // If using a template, get template details
      if (scheduleForm.template_id) {
        const template = templates.find(t => t.id === scheduleForm.template_id);
        if (template) {
          scheduleForm.start_time = template.start_time;
          scheduleForm.end_time = template.end_time;
          scheduleForm.break_duration_minutes = template.break_duration_minutes;
          scheduleForm.is_working_day = template.is_working_day;
        }
      }

      await schedulesAPI.createOrUpdateSchedule(scheduleForm);
      
      setShowScheduleModal(false);
      setScheduleForm({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        template_id: '',
        start_time: '',
        end_time: '',
        break_duration_minutes: 0,
        is_working_day: true,
        schedule_type: 'regular',
        comments: ''
      });
      
      fetchSchedules();
    } catch (error) {
      console.error('Error creating schedule:', error);
      setError('Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      employee_id: schedule.employee_id,
      date: schedule.date.split('T')[0],
      template_id: schedule.template_id || '',
      start_time: schedule.start_time || '',
      end_time: schedule.end_time || '',
      break_duration_minutes: schedule.break_duration_minutes || 0,
      is_working_day: schedule.is_working_day,
      schedule_type: schedule.schedule_type || 'regular',
      comments: schedule.comments || ''
    });
    setShowScheduleModal(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await schedulesAPI.deleteSchedule(scheduleId, { reason: 'Deleted by user' });
      fetchSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      setError('Failed to delete schedule');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleBulkOperation = async () => {
    try {
      setLoading(true);
      setBulkResults(null);

      let response;
      switch (bulkOperationType) {
        case 'create':
          response = await schedulesAPI.bulkCreateSchedules(bulkForm);
          break;
        case 'delete':
          response = await schedulesAPI.bulkDeleteSchedules({
            employee_ids: bulkForm.employee_ids,
            start_date: bulkForm.start_date,
            end_date: bulkForm.end_date,
            reason: bulkForm.comments || 'Bulk deletion'
          });
          break;
        case 'copy':
          response = await schedulesAPI.copySchedules(copyForm);
          break;
        default:
          throw new Error('Invalid bulk operation type');
      }

      setBulkResults(response.data);
      
      // Refresh schedules if we're on the calendar tab
      if (activeTab === 'calendar') {
        fetchSchedules();
      }
    } catch (error) {
      console.error('Error performing bulk operation:', error);
      setError(`Failed to ${bulkOperationType} schedules: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelection = (employeeId, checked) => {
    const currentIds = bulkOperationType === 'copy' ? copyForm.employee_ids : bulkForm.employee_ids;
    const updatedIds = checked 
      ? [...currentIds, employeeId]
      : currentIds.filter(id => id !== employeeId);

    if (bulkOperationType === 'copy') {
      setCopyForm({ ...copyForm, employee_ids: updatedIds });
    } else {
      setBulkForm({ ...bulkForm, employee_ids: updatedIds });
    }
  };

  const selectAllEmployees = () => {
    const allIds = employees.map(emp => emp.id);
    if (bulkOperationType === 'copy') {
      setCopyForm({ ...copyForm, employee_ids: allIds });
    } else {
      setBulkForm({ ...bulkForm, employee_ids: allIds });
    }
  };

  const clearAllEmployees = () => {
    if (bulkOperationType === 'copy') {
      setCopyForm({ ...copyForm, employee_ids: [] });
    } else {
      setBulkForm({ ...bulkForm, employee_ids: [] });
    }
  };

  const getScheduleTypeColor = (type) => {
    const colors = {
      regular: 'bg-blue-100 text-blue-800',
      holiday: 'bg-green-100 text-green-800',
      sick: 'bg-red-100 text-red-800',
      personal: 'bg-yellow-100 text-yellow-800',
      unscheduled: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.regular;
  };

  const renderScheduleCalendar = () => {
    // Group schedules by employee
    const schedulesByEmployee = schedules.reduce((acc, schedule) => {
      const key = `${schedule.first_name} ${schedule.last_name}`;
      if (!acc[key]) {
        acc[key] = {
          employee: {
            id: schedule.employee_id,
            name: key,
            employee_number: schedule.employee_number
          },
          schedules: []
        };
      }
      acc[key].schedules.push(schedule);
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {/* Date Range Controls */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Schedule Calendar</h3>
            {(isAdmin() || isManager() || isHRManager()) && (
              <button
                onClick={() => setShowScheduleModal(true)}
                className="btn-primary"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Schedule
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                className="form-input"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                className="form-input"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <div className="flex space-x-2 w-full">
                <button
                  onClick={() => {
                    const today = new Date();
                    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    setDateRange({
                      start_date: firstDay.toISOString().split('T')[0],
                      end_date: lastDay.toISOString().split('T')[0]
                    });
                  }}
                  className="btn-secondary flex-1"
                >
                  This Month
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                    const lastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                    setDateRange({
                      start_date: nextMonth.toISOString().split('T')[0],
                      end_date: lastDay.toISOString().split('T')[0]
                    });
                  }}
                  className="btn-secondary flex-1"
                >
                  Next Month
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const yearStart = new Date(today.getFullYear(), 0, 1);
                    const yearEnd = new Date(today.getFullYear(), 11, 31);
                    setDateRange({
                      start_date: yearStart.toISOString().split('T')[0],
                      end_date: yearEnd.toISOString().split('T')[0]
                    });
                  }}
                  className="btn-secondary flex-1"
                >
                  This Year
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Schedule List */}
        {Object.entries(schedulesByEmployee).length === 0 ? (
          <div className="card text-center py-12">
            <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedules Found</h3>
            <p className="text-gray-600">No schedules found for the selected date range.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(schedulesByEmployee).map(([employeeName, data]) => (
              <div key={data.employee.id} className="card">
                <div className="border-b border-gray-200 pb-4 mb-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    {employeeName}
                  </h4>
                  <p className="text-sm text-gray-600">
                    Employee ID: {data.employee.employee_number}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.schedules.map((schedule) => (
                    <div key={schedule.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(schedule.date).toLocaleDateString('en-GB')}
                        </span>
                        {(isAdmin() || isManager() || isHRManager()) && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleEditSchedule(schedule)}
                              className="p-1 text-gray-400 hover:text-blue-600"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Time:</span>
                          <span className="text-sm font-medium">
                            {schedule.is_working_day 
                              ? `${formatTime(schedule.start_time)} - ${formatTime(schedule.end_time)}`
                              : 'Non-working day'
                            }
                          </span>
                        </div>

                        {schedule.break_duration_minutes > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Break:</span>
                            <span className="text-sm font-medium">
                              {schedule.break_duration_minutes} min
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Type:</span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getScheduleTypeColor(schedule.schedule_type)}`}>
                            {schedule.schedule_type}
                          </span>
                        </div>

                        {schedule.template_name && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Template:</span>
                            <span className="text-sm font-medium">{schedule.template_name}</span>
                          </div>
                        )}

                        {schedule.comments && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <p className="text-xs text-gray-600">{schedule.comments}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTemplates = () => {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Schedule Templates</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div key={template.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">{template.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    template.is_working_day ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {template.is_working_day ? 'Working' : 'Non-working'}
                  </span>
                </div>

                {template.description && (
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                )}

                <div className="space-y-2">
                  {template.is_working_day && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Time:</span>
                        <span className="text-sm font-medium">
                          {formatTime(template.start_time)} - {formatTime(template.end_time)}
                        </span>
                      </div>
                      {template.break_duration_minutes > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Break:</span>
                          <span className="text-sm font-medium">
                            {template.break_duration_minutes} min
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderUnscheduledDays = () => {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Unscheduled Working Days</h3>
          </div>

          {unscheduledDays.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No unscheduled working days found for the selected period.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {unscheduledDays.map((day) => (
                    <tr key={`${day.employee_id}-${day.date}`}>
                      <td>
                        <div>
                          <div className="font-medium text-gray-900">
                            {day.first_name} {day.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {day.employee_number}
                          </div>
                        </div>
                      </td>
                      <td>{new Date(day.date).toLocaleDateString('en-GB')}</td>
                      <td>{formatTime(day.clock_in)}</td>
                      <td>{day.clock_out ? formatTime(day.clock_out) : 'Not clocked out'}</td>
                      <td>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          day.schedule_status === 'no_schedule' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {day.schedule_status === 'no_schedule' ? 'No Schedule' : 'Non-working Day'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => {
                            setScheduleForm({
                              ...scheduleForm,
                              employee_id: day.employee_id,
                              date: day.date.split('T')[0],
                              schedule_type: 'unscheduled'
                            });
                            setShowScheduleModal(true);
                          }}
                          className="btn-secondary text-sm"
                        >
                          Create Schedule
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderScheduleModal = () => {
    if (!showScheduleModal) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingSchedule ? 'Edit Schedule' : 'Create Schedule'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee
                </label>
                <select
                  className="form-input"
                  value={scheduleForm.employee_id}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, employee_id: e.target.value })}
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_number})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={scheduleForm.date}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  className="form-input"
                  value={scheduleForm.template_id}
                  onChange={(e) => {
                    const template = templates.find(t => t.id === e.target.value);
                    setScheduleForm({ 
                      ...scheduleForm, 
                      template_id: e.target.value,
                      start_time: template?.start_time || '',
                      end_time: template?.end_time || '',
                      break_duration_minutes: template?.break_duration_minutes || 0,
                      is_working_day: template?.is_working_day ?? true
                    });
                  }}
                >
                  <option value="">Custom Schedule</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_working_day"
                  className="form-checkbox"
                  checked={scheduleForm.is_working_day}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, is_working_day: e.target.checked })}
                />
                <label htmlFor="is_working_day" className="ml-2 text-sm text-gray-700">
                  Working Day
                </label>
              </div>

              {scheduleForm.is_working_day && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        className="form-input"
                        value={scheduleForm.start_time}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        className="form-input"
                        value={scheduleForm.end_time}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Break Duration (minutes)
                    </label>
                    <input
                      type="number"
                      className="form-input"
                      value={scheduleForm.break_duration_minutes}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, break_duration_minutes: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Type
                </label>
                <select
                  className="form-input"
                  value={scheduleForm.schedule_type}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_type: e.target.value })}
                >
                  <option value="regular">Regular</option>
                  <option value="holiday">Holiday</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal</option>
                  <option value="unscheduled">Unscheduled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comments
                </label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={scheduleForm.comments}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, comments: e.target.value })}
                  placeholder="Reason for schedule change or additional notes..."
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setEditingSchedule(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSchedule}
                disabled={!scheduleForm.employee_id || !scheduleForm.date}
                className="btn-primary"
              >
                {editingSchedule ? 'Update' : 'Create'} Schedule
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBulkOperations = () => {
    const currentEmployeeIds = bulkOperationType === 'copy' ? copyForm.employee_ids : bulkForm.employee_ids;
    
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Bulk Schedule Operations</h3>
              <p className="text-sm text-gray-600">Manage schedules for multiple employees and date ranges</p>
            </div>
          </div>

          {/* Operation Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Operation Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="bulkOperationType"
                  value="create"
                  checked={bulkOperationType === 'create'}
                  onChange={(e) => setBulkOperationType(e.target.value)}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700">Create Schedules</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="bulkOperationType"
                  value="delete"
                  checked={bulkOperationType === 'delete'}
                  onChange={(e) => setBulkOperationType(e.target.value)}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700">Delete Schedules</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="bulkOperationType"
                  value="copy"
                  checked={bulkOperationType === 'copy'}
                  onChange={(e) => setBulkOperationType(e.target.value)}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700">Copy Schedules</span>
              </label>
            </div>
          </div>

          {/* Employee Selection */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Select Employees ({currentEmployeeIds.length} selected)
              </label>
              <div className="flex space-x-2">
                <button onClick={selectAllEmployees} className="btn-secondary text-sm">
                  Select All
                </button>
                <button onClick={clearAllEmployees} className="btn-secondary text-sm">
                  Clear All
                </button>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {employees.map((employee) => (
                  <label key={employee.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={currentEmployeeIds.includes(employee.id)}
                      onChange={(e) => handleEmployeeSelection(employee.id, e.target.checked)}
                      className="form-checkbox"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {employee.first_name} {employee.last_name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Operation-specific forms */}
          {bulkOperationType === 'create' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={bulkForm.start_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={bulkForm.end_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                  <select
                    className="form-input"
                    value={bulkForm.template_id}
                    onChange={(e) => setBulkForm({ ...bulkForm, template_id: e.target.value })}
                  >
                    <option value="">Select Template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Pattern</label>
                  <select
                    className="form-input"
                    value={bulkForm.pattern_id}
                    onChange={(e) => setBulkForm({ ...bulkForm, pattern_id: e.target.value })}
                  >
                    <option value="">Select Pattern</option>
                    {weeklyPatterns.map((pattern) => (
                      <option key={pattern.id} value={pattern.id}>
                        {pattern.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Type</label>
                <select
                  className="form-input"
                  value={bulkForm.schedule_type}
                  onChange={(e) => setBulkForm({ ...bulkForm, schedule_type: e.target.value })}
                >
                  <option value="regular">Regular</option>
                  <option value="holiday">Holiday</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Options</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={bulkForm.options.skip_weekends}
                      onChange={(e) => setBulkForm({
                        ...bulkForm,
                        options: { ...bulkForm.options, skip_weekends: e.target.checked }
                      })}
                      className="form-checkbox"
                    />
                    <span className="ml-2 text-sm text-gray-700">Skip weekends</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={bulkForm.options.skip_existing}
                      onChange={(e) => setBulkForm({
                        ...bulkForm,
                        options: { ...bulkForm.options, skip_existing: e.target.checked }
                      })}
                      className="form-checkbox"
                    />
                    <span className="ml-2 text-sm text-gray-700">Skip existing schedules</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={bulkForm.options.overwrite_existing}
                      onChange={(e) => setBulkForm({
                        ...bulkForm,
                        options: { ...bulkForm.options, overwrite_existing: e.target.checked }
                      })}
                      className="form-checkbox"
                    />
                    <span className="ml-2 text-sm text-gray-700">Overwrite existing schedules</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {bulkOperationType === 'delete' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={bulkForm.start_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={bulkForm.end_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">Warning</h4>
                    <p className="text-sm text-red-700 mt-1">
                      This will permanently delete all schedules for the selected employees within the specified date range.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {bulkOperationType === 'copy' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Start Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={copyForm.source_start_date}
                    onChange={(e) => setCopyForm({ ...copyForm, source_start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source End Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={copyForm.source_end_date}
                    onChange={(e) => setCopyForm({ ...copyForm, source_end_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Start Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={copyForm.target_start_date}
                    onChange={(e) => setCopyForm({ ...copyForm, target_start_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Options</label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={copyForm.options.overwrite_existing}
                    onChange={(e) => setCopyForm({
                      ...copyForm,
                      options: { ...copyForm.options, overwrite_existing: e.target.checked }
                    })}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700">Overwrite existing schedules</span>
                </label>
              </div>
            </div>
          )}

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
            <textarea
              className="form-input"
              rows="3"
              value={bulkOperationType === 'copy' ? '' : bulkForm.comments}
              onChange={(e) => setBulkForm({ ...bulkForm, comments: e.target.value })}
              placeholder={`Reason for ${bulkOperationType} operation...`}
            />
          </div>

          {/* Execute Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleBulkOperation}
              disabled={currentEmployeeIds.length === 0 || loading}
              className={`btn-primary ${
                bulkOperationType === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''
              }`}
            >
              {loading ? 'Processing...' : `${bulkOperationType.charAt(0).toUpperCase() + bulkOperationType.slice(1)} Schedules`}
            </button>
          </div>
        </div>

        {/* Results Display */}
        {bulkResults && (
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Operation Results</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {bulkResults.results && (
                  <>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{bulkResults.results.total_employees || 0}</div>
                      <div className="text-sm text-gray-600">Employees</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{bulkResults.results.schedules_created || bulkResults.results.schedules_copied || 0}</div>
                      <div className="text-sm text-gray-600">Created/Copied</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">{bulkResults.results.schedules_updated || bulkResults.results.schedules_skipped || 0}</div>
                      <div className="text-sm text-gray-600">Updated/Skipped</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{bulkResults.results.errors?.length || bulkResults.deleted_schedules || 0}</div>
                      <div className="text-sm text-gray-600">Errors/Deleted</div>
                    </div>
                  </>
                )}
              </div>

              {bulkResults.results?.errors && bulkResults.results.errors.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Errors</h4>
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <ul className="text-sm text-red-700 space-y-1">
                      {bulkResults.results.errors.map((error, index) => (
                        <li key={index}>
                          Employee ID {error.employee_id}: {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              <div className="text-sm text-gray-600">
                <strong>Message:</strong> {bulkResults.message}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'calendar':
        return renderScheduleCalendar();
      case 'templates':
        return renderTemplates();
      case 'unscheduled':
        return renderUnscheduledDays();
      case 'bulk':
        return renderBulkOperations();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Employee Schedules</h1>
          <p className="text-sm text-gray-600 mt-1">Manage employee work schedules and shifts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`nav-item py-4 px-1 border-b-2 font-medium flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'nav-item-active border-blue-500 text-blue-600'
                      : 'nav-item-inactive border-transparent'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      {renderScheduleModal()}
    </div>
  );
};

export default Schedules;